import { ShieldAlert, ExternalLink, Sparkles, AlertTriangle, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const TIPO_LABELS = {
  asientos_descuadrados:       'Descuadre',
  asientos_huerfanos:          'Huérfanos',
  cuentas_iva_descuadradas:    'IVA fuera banda',
  cuentas_inactivas_grandes:   'Cuenta nueva grande',
};

const SEVERIDAD_CLS = {
  critico: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  alto:    'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  medio:   'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  info:    'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
};

/**
 * DetectAccountingPatternsResult — shown after detectar_patrones_sospechosos_contabilidad.
 * Read-only audit card. Cross-asiento detectors + Bedrock narrative.
 */
export default function DetectAccountingPatternsResult({ result }) {
  if (!result) return null;

  const patterns = result.patterns ?? [];
  const total = result.total ?? 0;

  if (total === 0) {
    return (
      <div className="rounded-xl border border-success/30 bg-success/10 px-3 py-2.5 flex items-center gap-2 my-1 max-w-sm">
        <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
        <div className="min-w-0">
          <p className="text-xs font-semibold text-foreground">
            Contabilidad limpia · {result.periodo}
          </p>
          <p className="text-[10px] text-muted-foreground">Sin patrones sospechosos en el período</p>
        </div>
      </div>
    );
  }

  const hasCriticos = (result.criticos ?? 0) > 0;
  const HeaderIcon = hasCriticos ? AlertCircle : AlertTriangle;
  const headerCls = hasCriticos
    ? 'border-destructive/30 bg-destructive/10'
    : 'border-warning/30 bg-warning/10';
  const iconCls = hasCriticos ? 'text-destructive' : 'text-warning';

  return (
    <div className={`rounded-xl border ${headerCls} p-3 my-1 max-w-sm space-y-2`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <HeaderIcon className={`w-4 h-4 ${iconCls} shrink-0`} />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-foreground">
              <ShieldAlert className="w-3 h-3 inline" /> Auditoría contable · {result.periodo}
            </p>
            <p className="text-[10px] text-muted-foreground">
              {total} patrón{total !== 1 ? 'es' : ''}
              {(result.criticos ?? 0) > 0 && ` · ${result.criticos} crítico${result.criticos !== 1 ? 's' : ''}`}
              {(result.warnings ?? 0) > 0 && ` · ${result.warnings} alto${result.warnings !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
        <Link to={result.deeplink || `/accounting/journal?periodo=${result.periodo}`}>
          <Button size="sm" variant="outline" className="h-7 text-[11px] shrink-0 gap-1">
            Ver
            <ExternalLink className="w-3 h-3" />
          </Button>
        </Link>
      </div>

      {result.narrativa && (
        <div className="rounded-md sf-surface p-2 border border-indigo-200 dark:border-indigo-800/60">
          <div className="flex items-center gap-1 mb-1">
            <Sparkles className="w-3 h-3 text-indigo-500" />
            <span className="text-[10px] uppercase tracking-wider font-medium text-indigo-700 dark:text-indigo-300">
              Diagnóstico ARIA
            </span>
          </div>
          <p className="text-xs text-foreground leading-snug">{result.narrativa}</p>
        </div>
      )}

      <div className="space-y-1.5 border-t border-border pt-2">
        {patterns.slice(0, 4).map((p, i) => (
          <div key={i} className="flex items-start gap-2 text-[11px]">
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium shrink-0 mt-0.5 ${SEVERIDAD_CLS[p.severidad] ?? SEVERIDAD_CLS.medio}`}>
              {TIPO_LABELS[p.tipo] ?? p.tipo}
            </span>
            <span className="flex-1 text-foreground leading-snug">
              {p.mensaje}
            </span>
          </div>
        ))}
        {patterns.length > 4 && (
          <p className="text-[10px] text-muted-foreground">…y {patterns.length - 4} más</p>
        )}
      </div>
    </div>
  );
}
